import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, Calendar, Clock, User, Share2, Bookmark, Loader2 } from "lucide-react";

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
      // Set sample post for demo
      setPost({
        title: "Understanding SIP: The Power of Systematic Investing",
        content: `Systematic Investment Plan (SIP) is a disciplined approach to investing in mutual funds. Instead of making a lump sum investment, you invest a fixed amount regularly (usually monthly).

## Benefits of SIP

1. **Rupee Cost Averaging**: When markets are down, your SIP buys more units. When markets are up, it buys fewer. Over time, this averages out the cost per unit.

2. **Power of Compounding**: Starting early with SIP allows your money to grow exponentially over time.

3. **Disciplined Investing**: SIP removes the emotional aspect of investing and creates a regular savings habit.

4. **Flexibility**: You can start with as little as ₹500 per month and increase over time.

## How to Start?

Choose funds based on your goals, risk appetite, and investment horizon. Use our SIP calculator to plan your investments.

## Key Considerations

- Start early to maximize the power of compounding
- Stay invested for the long term
- Review your investments periodically
- Increase SIP amount as your income grows`,
        category: "Investment Basics",
        author: "Paisa Secure",
        image_url: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800",
        created_at: "2024-01-15T10:00:00Z"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="font-serif text-2xl font-bold mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="font-semibold mt-4">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.match(/^\d+\./)) {
        return <p key={index} className="ml-4 mb-2">{line}</p>;
      }
      if (line.startsWith('- ')) {
        return <p key={index} className="ml-4 mb-2">• {line.replace('- ', '')}</p>;
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return <p key={index} className="text-muted-foreground leading-relaxed mb-4">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-28 pb-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-serif text-2xl font-bold mb-4">Post Not Found</h1>
            <Link to="/blog">
              <Button variant="outline" className="border-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <article className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Learning Center
          </Link>

          {/* Header */}
          <header className="mb-8">
            <Badge className="bg-primary text-primary-foreground mb-4">{post.category}</Badge>
            <h1 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-testid="post-title">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                5 min read
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            {renderContent(post.content)}
          </div>

          {/* Share Buttons */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Found this helpful? Share it!</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="border-white/10">
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="border-white/10">
                  <Bookmark className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* CTA Card */}
          <Card className="glass-card mt-12">
            <CardContent className="p-8 text-center">
              <h3 className="font-serif text-xl font-bold mb-3">
                Ready to Start Investing?
              </h3>
              <p className="text-muted-foreground mb-6">
                Use our calculators to plan your investment journey
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/calculators">
                  <Button className="btn-primary">Try SIP Calculator</Button>
                </Link>
                <Link to="/research">
                  <Button variant="outline" className="border-white/10">Browse Funds</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </article>
      </main>

      <Footer />
    </div>
  );
};
